import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from '../AppIcon';

/**
 * Reusable upgrade modal shown when a free user hits a usage limit.
 *
 * Props:
 *   isOpen: boolean
 *   onClose: () => void
 *   feature: 'ai_generate' | 'publish' | 'image_upload' | 'analytics'
 *   used: number (current usage)
 *   limit: number (max for free tier)
 *   daysUntilReset: number
 */
const UpgradeModal = ({ isOpen, onClose, feature, used, limit, daysUntilReset, message }) => {
  const navigate = useNavigate();

  if (!isOpen) return null;

  const featureConfig = {
    ai_generate: {
      icon: 'Sparkles',
      title: 'Generation Limit Reached',
      description: message || `You've used all ${limit} Basic plan content generations this month.`,
    },
    publish: {
      icon: 'Send',
      title: 'Publish Limit Reached',
      description: message || `You've used all ${limit} Basic plan publishes this month.`,
    },
    image_upload: {
      icon: 'Image',
      title: 'Pro Feature',
      description: 'Image uploads are available on the Pro plan.',
    },
    analytics: {
      icon: 'BarChart3',
      title: 'Pro Feature',
      description: 'Analytics dashboard is available on the Pro plan.',
    },
    schedule: {
      icon: 'Clock',
      title: 'Scheduling Limit Reached',
      description: message || `You've used your ${limit || 1} free scheduled post${limit !== 1 ? 's' : ''} this month.`,
    },
  };

  const config = featureConfig[feature] || featureConfig.ai_generate;

  const proFeatures = [
    'Unlimited AI content generations',
    'Unlimited publishing to all platforms',
    'Image attachments for posts',
    'Analytics dashboard',
    'Custom writing styles (unlimited)',
    'Priority support',
  ];

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          onClick={onClose}
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative w-full max-w-md bg-background border border-border rounded-2xl shadow-2xl overflow-hidden"
        >
          {/* Header gradient */}
          <div className="bg-gradient-to-r from-[#7E6BC4]/20 to-[#A78BFA]/20 px-6 pt-6 pb-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#A78BFA]/20">
                  <Icon name={config.icon} className="w-5 h-5 text-[#A78BFA]" />
                </div>
                <h3 className="text-lg font-semibold text-white">{config.title}</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-colors"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
              </button>
            </div>
            <p className="text-foreground text-sm">{config.description}</p>
            {daysUntilReset > 0 && limit > 0 && (
              <p className="text-muted-foreground text-xs mt-2">
                {feature === 'ai_generate' ? 'Basic' : 'Free'} quota resets in {daysUntilReset} day{daysUntilReset !== 1 ? 's' : ''}
              </p>
            )}
          </div>

          {/* Pro features */}
          <div className="px-6 py-4">
            <p className="text-sm font-medium text-muted-foreground mb-3">Upgrade to Pro and get:</p>
            <div className="space-y-2">
              {proFeatures.map((feat, i) => (
                <div key={i} className="flex items-center gap-2">
                  <Icon name="Check" className="w-4 h-4 text-emerald-400 flex-shrink-0" />
                  <span className="text-sm text-foreground">{feat}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="px-6 pb-6 flex flex-col gap-2">
            <button
              onClick={() => {
                onClose();
                navigate('/pricing');
              }}
              className="w-full py-2.5 px-4 bg-gradient-to-r from-[#7E6BC4] to-[#A78BFA] hover:opacity-90 text-white font-medium rounded-lg transition-all shadow-lg shadow-[#A78BFA]/20"
            >
              Upgrade to Pro
            </button>
            <button
              onClick={onClose}
              className="w-full py-2 px-4 text-muted-foreground hover:text-foreground text-sm transition-colors"
            >
              Maybe later
            </button>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default UpgradeModal;
